import { localForageHelper } from '@/hub-shared'
import Select, { Props, State } from '@components/inputs/select/select.component'
import Country from '@interfaces/country.interface'
import GeneralListingsService from '@services/general-listings/general-listings.service'
import LogService from '@services/log/log.service'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

export default ({
  label,
  disabled,
  errorSpan = true,
  required,
  externalError,
  id,
  name,
  leftLabel,
  rightLabel,
  externalIcon,
  errorIcon,
  value = '',
  placeholder,
  backgroundColor = '',
  autoComplete = 'off',
  onChange,
  onValidate,
  className = '',
  requiredOnLoad = false,
}: Props) => {
  const generalListingsService = useMemo(() => new GeneralListingsService(), [])
  const logService = useMemo(() => new LogService(), [])
  logService.init('InputCountries')

  const { t, i18n } = useTranslation('inputCountries')
  const [loadingCountries, setLoadingCountries] = useState(false)
  const [countries, setCountries] = useState<Country[]>([])
  const [validation, setValidation] = useState(false)
  const [state, setState] = useState<State>({
    value: '',
    error: '',
  })

  const getCountries = useCallback(async (): Promise<void> => {
    if (!loadingCountries) {
      setLoadingCountries(true)
      const langStorage: string = (await localForageHelper?.getItem('language')) || i18n?.language || 'en'
      const countriesData = await generalListingsService.getCountries(langStorage)
      setCountries(countriesData)
      setLoadingCountries(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generalListingsService, i18n])

  const handleChange = useCallback(
    (value: string): void => {
      setState((prevState) => ({ ...prevState, value }))
      if (onValidate && typeof onValidate === 'function') {
        onValidate(!externalError)
      }
      if (onChange && typeof onChange === 'function') {
        onChange(value)
      }
    },
    [externalError, onChange, onValidate]
  )

  useEffect(() => {
    try {
      getCountries()
    } catch (error) {
      logService.error({
        error,
        message: 'Failed to fetch countries',
      })
    }
  }, [getCountries, logService, setLoadingCountries, i18n.language])

  return (
    <Select
      label={label}
      placeholder={placeholder}
      value={value}
      className={className}
      name={name}
      id={id}
      loading={loadingCountries}
      onChange={handleChange}
      onValidate={(nationality: boolean) => setValidation(nationality)}
      externalError={externalError ? externalError : state.error}
      disabled={disabled}
      required={required}
      errorSpan={errorSpan}
      errorIcon={errorIcon}
      leftLabel={leftLabel}
      rightLabel={rightLabel}
      externalIcon={externalIcon}
      backgroundColor={backgroundColor}
      autoComplete={autoComplete}
      requiredOnLoad={requiredOnLoad}
    >
      {countries?.length
        ? countries?.map((country, index) => (
            <option key={index} value={country.code}>
              {country.name}
            </option>
          ))
        : null}
    </Select>
  )
}
